import { ITask } from 'Core/TaskAdapter'
import { ExpertsAttributesType } from 'Types/Attribute.types'
import {
    TaskAttribute,
    AttributeDefinitionType,
    ContainerDataType,
    ContainerType,
    RatingsOnLinearConversationDataType,
    TaskConfigType,
    ConversationNode,
} from 'Types/Tasks.types'
import { Studio } from 'Core/Studio'
import { createReactElement } from 'shared/utils/react.utils'
import { RatingsTaskUi } from 'Tasks/RatingsTask/UI/RatingsTaskUI'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { isEmpty } from 'lodash'
import { RatingsContainerStrategy } from 'Core/StateContainerCore/ContainerStrategies/RatingsContainerStrategy'
import { ChecklistIcon } from 'Components/common/Icons/ChecklistIcon'
import { RatingsAttribute } from 'Components/tasks/RatingsAttribute/RatingsAttribute'
import { ITaskBlock } from 'shared/express-interface/blocks-core/ITaskBlock'

export class RatingsTask
    extends CommonTask<
        ContainerType.RatingsOnLinearConversation,
        RatingsOnLinearConversationDataType
    >
    implements
        ITask<
            TaskConfigType<
                ContainerType.RatingsOnLinearConversation,
                RatingsOnLinearConversationDataType
            >
        >
{
    private selectedResponseId?: string
    private hoveredResponseId?: string

    constructor(protected studio: Studio) {
        const ratingsContainerStrategy = new RatingsContainerStrategy()
        super(studio, ratingsContainerStrategy)
        ratingsContainerStrategy.setContext({
            task: this,
        })
    }

    public initialize(
        config: TaskConfigType<
            ContainerType.RatingsOnLinearConversation,
            RatingsOnLinearConversationDataType
        >
    ) {
        const promptBlock = this.studio.blockEngine.addBlock('prompt-block', {
            id: 'prompt-block-1',
            name: 'prompt-block-1',
        })

        const blocks: ITaskBlock[] = [promptBlock]

        if (config.assignment.task.input_container.data.data.responses?.length) {
            const selectBlock = this.studio.blockEngine.addBlock('select-block', {
                id: 'select-block-1',
                name: 'select-block-1',
                componentProps: {
                    options: [
                        { label: 'Single Row View', value: 'single-row-view' },
                        { label: 'Grid View', value: 'grid-view' },
                    ],
                    initialValue: 'single-row-view',
                },
            })
            const responsesBlock = this.studio.blockEngine.addBlock('responses-block', {
                id: 'responses-block-1',
                name: 'responses-block-1',
                componentProps: {
                    responses: config.assignment.task.input_container.data.data
                        .responses as ConversationNode[],
                    layout: selectBlock.getState('selectedValue'),
                    updateSelectedResponseId: (id: string) => {
                        this.selectedResponseId = id
                        const responseRatingGroups =
                            this.attributeManager?.getAttributeGroupsByDefinitionType(
                                AttributeDefinitionType.RESPONSE_RATING
                            )
                        responseRatingGroups?.forEach(group => {
                            group.render()
                        })
                    },
                    updateHoveredResponseId: (id: string) => {
                        this.hoveredResponseId = id
                        const responseRatingGroups =
                            this.attributeManager?.getAttributeGroupsByDefinitionType(
                                AttributeDefinitionType.RESPONSE_RATING
                            )
                        responseRatingGroups?.forEach(group => {
                            group.render()
                        })
                    },
                },
            })
            const flexBlock = this.studio.blockEngine.addBlock('flex-block', {
                id: 'flex-block-1',
                name: 'flex-block-1',
                componentProps: {
                    gap: 10,
                    vertical: true,
                    style: {
                        margin: '10px',
                    },
                    children: [
                        selectBlock.getElement()?.element,
                        responsesBlock.getElement()?.element,
                    ],
                },
            })

            blocks.push(flexBlock)
        }

        this.studio.workbench.enqueueUiUpdate(workbenchUi => {
            this.ui = createReactElement(RatingsTaskUi, {
                blocks,
            })

            this.addCommonWorkbenchComponents(workbenchUi, config)

            if (config.assignment.task.attributes) {
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.QUALITY_EVALUATION,
                    groupKey: 'quality-evaluation',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.QUALITY_EVALUATION
                    ),
                    config: { useInCollection: true },
                })
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.EXTRA_INFO,
                    groupKey: 'extra-info',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.EXTRA_INFO
                    ),
                    config: { useInCollection: true },
                })

                this.addRatingsAttributeElement()

                workbenchUi.rightSidebar.addDivider()
            }
        })
    }

    getClickedResponseId = () => this.selectedResponseId

    getHoveredResponseId = () => this.hoveredResponseId

    private addRatingsAttributeElement() {
        const config = this.initialConfig as TaskConfigType<
            ContainerType.RatingsOnLinearConversation,
            RatingsOnLinearConversationDataType
        >

        const ratingsAttributes = config.assignment.task.attributes.filter(
            attr => attr.type === AttributeDefinitionType.RESPONSE_RATING
        )

        if (isEmpty(ratingsAttributes)) {
            return
        }

        const ratingsAttributeGroupConfig = {
            type: AttributeDefinitionType.RESPONSE_RATING,
            groupKey: 'response-rating',
            attributes: ratingsAttributes,
            config: {
                useInCollection: false,
                taskProps: {
                    getClickedResponseId: this.getClickedResponseId,
                    getHoveredResponseId: this.getHoveredResponseId,
                },
            },
        }
        this.attributeManager?.useAttributeGroup(ratingsAttributeGroupConfig)
        this.attributeManager?.defineDependencies([
            {
                source: AttributeDefinitionType.EXTRA_INFO,
                dependents: [AttributeDefinitionType.RESPONSE_RATING],
            },
        ])

        const ratingsAttributeGroups = this.attributeManager?.getAttributeGroupsByDefinitionType(
            AttributeDefinitionType.RESPONSE_RATING
        )

        const ratingAttributeElement = ratingsAttributeGroups?.[0].getElement()
        if (!ratingAttributeElement) {
            return
        }
        const ratingsAttributeContainer = createReactElement(RatingsAttribute, {
            element: ratingAttributeElement,
        })
        this.studio.workbench.workbenchUiAccessor?.rightSidebar.addItem({
            id: 'ratings-attribute',
            element: ratingsAttributeContainer.element,
            title: 'Rate the Responses',
            icon: ChecklistIcon,
        })
    }

    protected getAttributesForFeedback = (
        taskAttributes: TaskAttribute[] | undefined,
        taskOutputStartData:
            | ContainerDataType<
                  ContainerType.RatingsOnLinearConversation,
                  RatingsOnLinearConversationDataType
              >
            | undefined
    ) => {
        const attributesList: ExpertsAttributesType[] = []
        const qcAttributes = taskAttributes?.filter(
            attribute => attribute.type === AttributeDefinitionType.QUALITY_EVALUATION
        )
        const qcAttributesValues = Object.values(
            (taskOutputStartData?.data?.data as RatingsOnLinearConversationDataType).quality_check
                ?.evaluation_on_version_id ?? {}
        )[0]

        if (isEmpty(qcAttributes) || isEmpty(qcAttributesValues)) {
            return attributesList
        }

        qcAttributes?.forEach(attribute => {
            if (qcAttributesValues?.[attribute.name]?.data.value) {
                attributesList.push({
                    label: attribute.label,
                    value: qcAttributesValues[attribute.name].data.value,
                    config: attribute,
                })
            }
        })

        return attributesList
    }
}
